import { useCallback, useEffect, useRef } from 'react'
const DEFAULT_INTERVAL = 3000

type UseIntervalOptions = {
  interval?: number // In ms (default: 3000)
  startWhen?: boolean // If false, the interval will not start (default: true)
  exitWhen?: boolean // If this condition is true, the interval will be cleared
  startWithoutDelay?: boolean // When true, the callback will be called at second 0 before starting the interval (default: true)
  attempts?: number // The number of attempts before clearing the interval
}

type ReturnType = {
  hasTimedOut: boolean
  nbOfAttempts: number
}

const useInterval = (
  callback: () => Promise<void>, // The callback to be called every interval
  { interval = DEFAULT_INTERVAL, startWhen = true, exitWhen, startWithoutDelay = true, attempts }: UseIntervalOptions
): ReturnType => {
  const attemptsRef = useRef(0)

  const callAndIncrement = useCallback(() => {
    callback()
    attemptsRef.current++
  }, [callback])

  useEffect(() => {
    if (!startWhen) return

    // Start without delay except if specified otherwise
    if (startWithoutDelay && attemptsRef.current === 0) {
      callAndIncrement()
    }
    // Start the setInterval
    const callBackInterval = setInterval(() => {
      const forceExit = attempts && attemptsRef.current >= attempts
      // If the condition to stop the interval is met, clear the interval
      if (exitWhen || forceExit) return () => clearInterval(callBackInterval)
      callAndIncrement()
    }, interval)

    return () => clearInterval(callBackInterval)
  }, [interval, startWithoutDelay, exitWhen, attempts, startWhen, callAndIncrement])

  const hasTimedOut = !!attempts && attemptsRef.current >= attempts
  return { hasTimedOut, nbOfAttempts: attemptsRef.current }
}

export default useInterval
